import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import OtpVerify from '../OtpVerify/OtpVerify';
import './SignUpForm.scss';

import closeIcon from '../../../../../Assets/images/close-icon.svg';
import nextIcon from '../../../../../Assets/images/next-icon.svg';

import { isValidEmail } from '../../../../../Utils/Validations/validateEmail';
import { isValidCanadianPhone } from '../../../../../Utils/Validations/validatePhone';
import { isValidPassword } from '../../../../../Utils/Validations/validatePassword';
import { FIELD_MAX_LENGTHS } from '../../Utils/Constants';
import PasswordTooltip from '../../../../../Utils/PasswordToolTip/PasswordToolTip';
import Loader from '../../../../../Context/Loader/Loader';
import TermsModal from '../TermsModal/TermsModal';
import { store } from '../../../../../Services/localStorageService';
import { jwtDecode } from 'jwt-decode';
import { showErrorToast } from '../../../../../Context/Toaster/Toaster';
import { getFirstPermittedRoute } from '../../Services/NavigationList';

const basePath = process.env.REACT_APP_SERVER_BASE_URL;
const signUpPath = process.env.REACT_APP_TC_SIGNUP_BASE_PATH;

const SignupForm = ({ onClose, onOpenSignin }) => {
  const navigate = useNavigate();
  const passwordWrapperRef = useRef(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    contact: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    willDrive: false,
  });

  const [errors, setErrors] = useState({});
  const [showOtp, setShowOtp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState({ password: false, confirmPassword: false });
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (passwordWrapperRef.current && !passwordWrapperRef.current.contains(event.target)) {
        setIsPasswordFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    const { firstName, lastName, email, contact, password, confirmPassword, companyName } = formData;

    if (!firstName.trim()) newErrors.firstName = 'First Name is required';
    if (!lastName.trim()) newErrors.lastName = 'Last Name is required';

    if (!email.trim()) newErrors.email = 'Email is required';
    else if (!isValidEmail(email)) newErrors.email = 'Invalid email format';

    if (!contact.trim()) newErrors.contact = 'Contact number is required';
    else if (!isValidCanadianPhone(contact)) newErrors.contact = 'Invalid Canadian phone number';

    if (!password) newErrors.password = 'Password is required';
    else {
      const { valid, message } = isValidPassword(password);
      if (!valid) newErrors.password = "Password doesn't meet the required criteria.";
    }

    if (!confirmPassword) newErrors.confirmPassword = 'Confirm Password is required';
    else if (password !== confirmPassword) newErrors.confirmPassword = 'Passwords do not match';

    if (!companyName.trim()) newErrors.companyName = 'Company Name is required';

    if (!termsAccepted) newErrors.termsAccepted = 'You must accept the terms and conditions.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const sendOtpToEmail = async (email_id, first_name, last_name) => {
    return await axios.post(`${basePath}${signUpPath}/send-otp`, { email_id, first_name, last_name });
  };

  const handleNext = async () => {
    if (!validate()) return;
    setIsLoading(true);
    try {
      const { data } = await sendOtpToEmail(formData.email, formData.firstName, formData.lastName);
      if (data.status === 'SUCCESS') {
        setShowOtp(true);
      } else {
        const msg = data?.message || 'Failed to send OTP';
        console.error(msg);
        showErrorToast(msg);
      }
    } catch (error) {
      const msg = error?.response?.data?.message || 'Failed to send OTP';
      console.error(msg);
      showErrorToast(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpVerify = async (otp) => {
    setIsLoading(true);
    const payload = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      contact_no: formData.contact,
      email_id: formData.email,
      password: formData.password,
      confirm_password: formData.confirmPassword,
      companyName: formData.companyName,
      otp,
      will_drive: formData.willDrive,
    };
    try {
      const { data } = await axios.post(`${basePath}${signUpPath}/create`, payload);
      if (data.status === 'SUCCESS') {


        const acessDecoded = jwtDecode(data.access_token);
        store('accessToken', data.access_token);
        store("accessTokenExpiry", acessDecoded.exp * 1000);

        const refreshDecoded = jwtDecode(data.refresh_token);
        store("refreshToken", data.refresh_token);
        store("refreshTokenExpiry", refreshDecoded.exp * 1000);

        store('companyName', formData.companyName)

        const firstPath = getFirstPermittedRoute();
        if (firstPath) {
          navigate(firstPath);
        } else {
          navigate("/access-denied")
        }
      } else {
        showErrorToast(data.message);
      }
    } catch (err) {
      const msg = err.response ? err.response.data.message : err.message;
      console.error(msg);
      showErrorToast(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpResend = async () => {
    try {
      await sendOtpToEmail(formData.email, formData.firstName, formData.lastName);
    } catch (err) {
      const msg = err.response ? err.response.data.message : err.message;
      console.error(msg);
      showErrorToast(msg);
    }
  };

  const renderInput = (label, name, type = 'text') => {
    const isPassword = name === 'password' || name === 'confirmPassword';
    const show = showPassword[name];

    return (
      <div className={`hf-split ${name === 'companyName' ? 'c-name' : ''}`}>
        <label className="fi-label">
          {label} <span>*</span>
        </label>
        <div className={`input-wraper pass-input ${name === 'password' && isPasswordFocused ? 'focused' : ''}`} style={{ position: 'relative', width: '100%' }} ref={name === 'password' ? passwordWrapperRef : null} >
          {name === 'password' && isPasswordFocused && !formData[name] && <PasswordTooltip />}

          <input type={isPassword ? (show ? 'text' : 'password') : type} name={name} value={formData[name]} onChange={handleChange} onFocus={() => name === 'password' && setIsPasswordFocused(true)} onBlur={() => name === 'password' && setIsPasswordFocused(false)} maxLength={FIELD_MAX_LENGTHS[name]} className={`common-input ${errors[name] ? 'error' : ''}`} />

          {isPassword && (
            <i className={`fa field-icon ${show ? 'fa-eye' : 'fa-eye-slash'}`} onClick={() => setShowPassword(prev => ({ ...prev, [name]: !show }))} style={{ position: 'absolute', top: '50%', right: '10px', transform: 'translateY(-50%)', cursor: 'pointer', }} />
          )}
        </div>

        <span className={`error-text ${errors[name] ? 'active' : ''}`}>{errors[name] || ' '}</span>
      </div>
    );
  };

  return (
    <>
      <div className="form-cvr">
        <div className="multi-container">
          <div className="form-outer">
            <form>
              <div className="page slide-page">
                {!showOtp ? (
                  <div className="h-form">
                    <button className="close-button" type="button" onClick={onClose}>
                      <img src={closeIcon} alt="close" />
                    </button>
                    <h1>Welcome to HaulSync</h1>
                    <h2>Sign Up to</h2>
                    <p>Truck Company</p>
                    <div className="hform-inner">
                      {renderInput('First Name', 'firstName')}
                      {renderInput('Last Name', 'lastName')}
                      {renderInput('Email address', 'email', 'email')}
                      {renderInput('Contact Number', 'contact', 'tel')}
                      {renderInput('Password', 'password')}
                      {renderInput('Confirm Password', 'confirmPassword')}
                      {renderInput('Company Name', 'companyName')}
                    </div>
                    <div className='signup-checks'>
                      <div className="form-check d-flex align-items-start">
                        <input
                          type="checkbox" className="form-check-input check-element" id="willDriveCheckbox" checked={formData.willDrive}
                          onChange={(e) => setFormData(prev => ({ ...prev, willDrive: e.target.checked }))} style={{ marginTop: '3px', marginRight: '8px' }} />
                        <label className="form-check-label" htmlFor="willDriveCheckbox">Make me driver</label>
                      </div>
                      <div className="form-check d-flex align-items-start mb-2">
                        <input type="checkbox" className="form-check-input check-element" id="termsCheckbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} style={{ marginTop: '3px', marginRight: '8px' }} />
                        <label className="form-check-label" htmlFor="termsCheckbox" style={{ flex: 1 }}>
                          I have read and I accept{' '}
                          <span style={{ color: '#59A52C', textDecoration: 'none', cursor: 'pointer', }} onClick={() => setShowTermsModal(true)}>
                            Terms and Conditions
                          </span>
                        </label>
                      </div>
                    </div>
                    <span className={`error-text ${errors.termsAccepted ? 'active' : ''}`}>
                      {errors.termsAccepted || ' '}
                    </span>
                    <div className='signup-buttons'>
                      <div className="field">
                        <button type="button" className={`firstNext next h-btn ${!termsAccepted ? 'disabled-btn' : ''}`} onClick={handleNext} disabled={isLoading || !termsAccepted}>
                          {isLoading ? <Loader /> : 'Next'}
                          {!isLoading && <img src={nextIcon} alt="Next" />}
                        </button>
                        <p className="ins-text">Already have an account? <a href="#" onClick={(e) => { e.preventDefault(); onOpenSignin(); }}>Sign In</a></p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <OtpVerify
                    email={formData.email}
                    onOtpSuccess={handleOtpVerify}
                    onBack={() => setShowOtp(false)}
                    onResend={handleOtpResend}
                  />
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAccept={() => {
          setTermsAccepted(true);
          setShowTermsModal(false);
        }}
        onDecline={() => {
          setTermsAccepted(false);
          setShowTermsModal(false);
        }}
      />
    </>
  );
};

export default SignupForm;
